import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';
import { fmtMins, fmtClock, nyMidnightOfYmd, todayStr } from '../lib/format.js';
import { locationKeyFromName } from '../lib/schedule.js';
import { ManualHoursButton } from './ManualHoursButton.jsx';
import { CorrectionRequestQueue } from './CorrectionRequestQueue.jsx';
import { PerfStat } from './PerfStat.jsx';
import { EditableTime } from './EditableTime.jsx';
import { timeclockEnabled } from '../lib/flags.js';
import { summarizeSquareSync } from '../lib/sync-summary.js';
import { toast, errText } from '../lib/toast.js';

// ─── Time Card Review panel (Round 9) ─────────────────────────────────────────
// Manager review of a single location-week. Shows:
//   - Header: scheduled / actual / delta / cost-of-delta
//   - Per-staff rows with the shifts they worked here (editable times)
//   - Edit log of all manager-side time clock changes
//   - Certify button (one per location-week)
// Edits are journaled to /api/timecard/edit and never pushed back to Square (Square
// stays the timecard source of truth; ops-suite keeps its own audit trail). PTO accrues
// automatically when the *last* location a staff worked at gets certified for the week.
export function TimecardReviewPanel({ location, weekStart, currentUser, onClose, onCertified }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(true);
  const [certBusy, setCertBusy] = useState(false);
  const [certResult, setCertResult] = useState(null);
  const [editing, setEditing] = useState(null); // {personIdx, shiftIdx, fieldId, value}
  // Café list for the "move a punch to the right store" control (CJ 2026-07-28:
  // Emily clocked in at JT Connell for a training that was actually Broadway).
  const [allLocations, setAllLocations] = useState([]);
  const [movingKey, setMovingKey] = useState(null); // 'pi|si' currently being moved
  useEffect(() => {
    api('GET', '/api/locations', null, headers)
      .then((r) => setAllLocations(Array.isArray(r) ? r : []))
      .catch(() => setAllLocations([]));
  }, []);
  // CJ feedback 2026-06-15: managers need to add a SECOND punch on a
  // day (split-shift case: staffer clocked in 5:30am, clocked out at
  // lunch, forgot to clock back in 12-2pm). Track which row's inline
  // form is open + its draft times.
  const [addPunchRow, setAddPunchRow] = useState(null); // 'pi|si' key
  const [addPunchForm, setAddPunchForm] = useState({ in_time: '', out_time: '' });
  const [addPunchBusy, setAddPunchBusy] = useState(false);
  // CJ feedback 2026-06-30: also need a way to add a punch for someone
  // who wasn't on the schedule at all (Kaela picked up a shift she
  // wasn't assigned to). The split-shift inline form attaches to an
  // existing row; this modal stands on its own.
  const [unschedOpen, setUnschedOpen] = useState(false);
  const [unschedUsers, setUnschedUsers] = useState([]);
  const [unschedForm, setUnschedForm] = useState({ user_id: '', date: '', in_time: '', out_time: '' });
  const [unschedBusy, setUnschedBusy] = useState(false);
  // E5: three view modes for the same staff-shift data.
  //   week     — every staff member, every shift in the week (legacy)
  //   day      — every staff member's shifts for a single picked day
  //   employee — one selected staff member, full week
  const [viewMode, setViewMode] = useState('week');
  const weekDays = (() => {
    const d = new Date(`${weekStart}T12:00:00Z`);
    const out = [];
    for (let i = 0; i < 7; i++) {
      out.push(new Date(d.getTime() + i * 86400000).toISOString().slice(0, 10));
    }
    return out;
  })();
  const todayYmd = todayStr();
  const [selectedDay, setSelectedDay] = useState(
    weekDays.includes(todayYmd) ? todayYmd : weekDays[0]
  );
  const headers = { 'x-user-id': String(currentUser.id) };
  const locKey = locationKeyFromName(location.name);

  async function load() {
    setBusy(true); setError('');
    try {
      const r = await api('GET', `/api/timecard/review/${locKey}?week_start=${weekStart}`, null, headers);
      setData(r);
    } catch (e) { setError(e.message || 'Failed to load review'); }
    finally { setBusy(false); }
  }
  useEffect(() => { load(); }, [locKey, weekStart]);
  // CJ feedback 2026-06-30: after certifying JT, switching to Bellevue
  // kept certResult populated → the certify button stayed disabled
  // ("nothing happens on click"). Reset the per-week state whenever
  // location or week changes.
  useEffect(() => {
    setCertResult(null);
    setAddPunchRow(null);
    setAddPunchForm({ in_time: '', out_time: '' });
  }, [locKey, weekStart]);

  async function saveEdit(person, shift, field, newValue) {
    const before = field === 'clock_in' ? shift.actualStart : shift.actualEnd;
    if ((before || '') === (newValue || '')) { setEditing(null); return; }
    try {
      await api('PUT', '/api/timecard/edit', {
        location_id: data.meta.location.id,
        week_start: weekStart,
        user_id: person.user_id,
        shift_date: shift.date,
        square_shift_id: shift.square_shift_id || null,
        punch_id: shift.monarch_punch_id || null,
        field,
        before_value: before || null,
        after_value: newValue || null,
      }, headers);
      setEditing(null);
      await load();
    } catch (e) { toast.error(errText(e, 'Edit failed')); }
  }

  // Move a punch to the right café (its hours follow to that store's week).
  async function moveLocation(person, shift, destId) {
    if (!destId || !shift.monarch_punch_id) return;
    setMovingKey(shift.monarch_punch_id);
    try {
      await api('PUT', '/api/timecard/edit', {
        location_id: data.meta.location.id,
        week_start: weekStart,
        user_id: person.user_id,
        shift_date: shift.date,
        punch_id: shift.monarch_punch_id,
        field: 'location',
        after_value: String(destId),
        note: 'Location corrected from Time Card Review',
      }, headers);
      await load();
    } catch (e) { toast.error(errText(e, 'Move failed')); }
    finally { setMovingKey(null); }
  }

  // CJ feedback 2026-06-15: split-shift forgot-to-clock-back-in case.
  // Manager adds a second punch for a (user, date) inline. Posts to
  // the existing /api/admin/timeclock/punches endpoint with the row's
  // user_id + location + date and the times the manager typed.
  async function addPunch(person, shift) {
    const { in_time, out_time } = addPunchForm;
    if (!in_time || !out_time) {
      toast.error('Need both a clock-in and clock-out time.');
      return;
    }
    if (in_time >= out_time) {
      toast.error('Clock-out must be after clock-in.');
      return;
    }
    function toIso(date, hm) {
      const [h, m] = hm.split(':').map(Number);
      const d = new Date(`${date}T${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:00`);
      return d.toISOString();
    }
    setAddPunchBusy(true);
    try {
      await api('POST', '/api/admin/timeclock/punches', {
        user_id: person.user_id,
        location_id: data.meta.location.id,
        clock_in_at:  toIso(shift.date, in_time),
        clock_out_at: toIso(shift.date, out_time),
        note: 'Added by manager from Time Card Review',
      }, headers);
      setAddPunchRow(null);
      setAddPunchForm({ in_time: '', out_time: '' });
      load();
    } catch (e) { toast.error(errText(e, 'Add-punch failed')); }
    finally { setAddPunchBusy(false); }
  }

  async function openUnsched() {
    // Default the date picker to the first day of the review week.
    const defaultDate = weekDays[0];
    setUnschedForm({ user_id: '', date: defaultDate, in_time: '', out_time: '' });
    setUnschedOpen(true);
    // Lazy-load the team list the first time the modal opens.
    if (unschedUsers.length === 0) {
      try {
        const r = await api('GET', '/api/users', null, headers);
        setUnschedUsers(Array.isArray(r) ? r : []);
      } catch (e) { /* swallow — modal still usable for retry */ }
    }
  }

  async function saveUnsched() {
    const { user_id, date, in_time, out_time } = unschedForm;
    if (!user_id || !date || !in_time) {
      toast.error('Pick a staff member, date, and clock-in time.');
      return;
    }
    if (out_time && in_time >= out_time) {
      toast.error('Clock-out must be after clock-in.');
      return;
    }
    function toIso(d, hm) {
      const [h, m] = hm.split(':').map(Number);
      const dt = new Date(`${d}T${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:00`);
      return dt.toISOString();
    }
    setUnschedBusy(true);
    try {
      await api('POST', '/api/admin/timeclock/punches', {
        user_id: Number(user_id),
        location_id: data.meta.location.id,
        clock_in_at:  toIso(date, in_time),
        clock_out_at: out_time ? toIso(date, out_time) : null,
        note: 'Unscheduled punch added from Time Card Review',
      }, headers);
      setUnschedOpen(false);
      await load();
    } catch (e) {
      let m = e?.message || 'Save failed';
      try { const p = JSON.parse(m); if (p?.error) m = p.error; } catch (_) {}
      toast.error(m);
    } finally { setUnschedBusy(false); }
  }

  // CJ feedback 2026-06-30: mark a scheduled shift as missed. Two
  // kinds — excused (approved absence, no penalty) or unexcused
  // (no-call-no-show, emits reliability incident on the server).
  // Prompts for a reason so the audit log is useful later.
  async function markAbsent(userId, shiftDate, kind) {
    const label = kind === 'excused' ? 'excused absence' : 'no-call-no-show';
    const reason = window.prompt(`Reason for marking ${shiftDate} as ${label}? (optional)`);
    if (reason === null) return; // user cancelled the prompt
    try {
      await api('POST', '/api/admin/timeclock/shift-absences', {
        user_id: userId,
        location_id: data.meta.location.id,
        shift_date: shiftDate,
        kind,
        reason: reason || null,
      }, headers);
      await load();
    } catch (e) {
      let m = e?.message || 'Mark absent failed';
      try { const p = JSON.parse(m); if (p?.error) m = p.error; } catch (_) {}
      toast.error(m);
    }
  }

  // CJ feedback 2026-06-30: delete a wrong punch outright (test row,
  // duplicate, never-clocked-back-in stray). Hard delete on the
  // server — cascades clean up breaks + audit, correction history
  // gets SET NULL so the request survives.
  async function deletePunch(person, shift) {
    if (!shift.monarch_punch_id) return;
    const when = `${shift.date}${shift.actualStart ? ' ' + fmtClock(shift.actualStart) : ''}`;
    if (!confirm(`Delete this punch for ${person.name} (${when})?\nThis cannot be undone.`)) return;
    try {
      await api('DELETE', `/api/admin/timeclock/punches/${shift.monarch_punch_id}`, null, headers);
      await load();
    } catch (e) {
      let m = e?.message || 'Delete failed';
      try { const p = JSON.parse(m); if (p?.error) m = p.error; } catch (_) {}
      toast.error(m);
    }
  }

  // Pull this week's punches from Square on demand (CJ 2026-07-30). The payroll
  // loop is: review here → spot an issue → fix it in Square → click this → the
  // week re-converges to Square and re-renders → certify. Uses the existing
  // idempotent one-way sync; no tab-switching to Payroll needed.
  const [syncBusy, setSyncBusy] = useState(false);
  const [syncMsg, setSyncMsg] = useState('');
  async function syncFromSquare() {
    if (syncBusy || certified) return;
    setSyncBusy(true); setSyncMsg('');
    try {
      const r = await api('POST', '/api/admin/timeclock/sync-from-square', { week_start: weekStart }, headers);
      await load();
      const s = summarizeSquareSync(r); setSyncMsg(s.text); if (s.ok) setTimeout(() => setSyncMsg(''), 4000);
    } catch (e) {
      let m = e?.message || 'Sync failed';
      try { const p = JSON.parse(m); if (p?.error) m = p.error; } catch (_) {}
      setSyncMsg(m);
    } finally { setSyncBusy(false); }
  }

  async function certify() {
    if (certBusy) return;
    setCertBusy(true);
    try {
      const r = await api('POST', `/api/timecard/certify/${locKey}`,
        { week_start: weekStart }, headers);
      setCertResult(r);
      if (onCertified) onCertified(r);
    } catch (e) { toast.error(errText(e, 'Certify failed')); }
    finally { setCertBusy(false); }
  }

  // Un-certify (CJ 2026-07-30): reverses the certification + its PTO accrual and
  // reliability emission, so the manager can reopen a week to fix something and
  // re-certify without double-counting. Confirmed because it moves paid balances.
  const [uncertBusy, setUncertBusy] = useState(false);
  async function uncertify() {
    if (uncertBusy) return;
    if (!confirm(`Un-certify ${location.name}, week of ${weekStart}? This reopens the week and reverses its PTO accrual for that week. You can re-certify after fixing.`)) return;
    setUncertBusy(true);
    try {
      const r = await api('POST', `/api/timecard/uncertify/${locKey}`, { week_start: weekStart }, headers);
      const n = r?.reversedAccruals?.length || 0;
      setCertResult(null);
      await load();
      if (onCertified) onCertified(r); // let the parent refresh its certified badge
      toast.success(`Week reopened.${n ? ` Reversed PTO accrual for ${n} ${n === 1 ? 'person' : 'people'}.` : ''}`);
    } catch (e) {
      let m = e?.message || 'Un-certify failed';
      try { const p = JSON.parse(m); if (p?.error) m = p.error; } catch (_) {}
      toast.error(m);
    } finally { setUncertBusy(false); }
  }

  if (busy && !data) return (
    <div className="card"><div className="text-sm text-gray-500">Loading review…</div></div>
  );
  if (error) return (
    <div className="card" style={{ borderLeft: '4px solid #c62828' }}>
      <div className="text-sm font-semibold text-red-700">Review unavailable</div>
      <div className="text-xs text-gray-600 mt-1">{error}</div>
      <div style={{ marginTop: 8 }}><button onClick={onClose} className="btn-secondary" style={{ fontSize: 12 }}>Close</button></div>
    </div>
  );
  if (!data) return null;

  const { totals, staff, edits, certification } = data;
  const certified = !!certification;
  // CJ 2026-07-29: Square is the clock now, so punches are read-only in Monarch.
  // Punch editing hides when the clock's off (same as when a week is certified);
  // reviewing, marking absences, and certifying stay available.
  const timeclockOn = timeclockEnabled();
  const canEditPunches = !certified && timeclockOn;
  const overUnder = totals.deltaHours >= 0 ? 'over' : 'under';
  const dragOnDelta = staff.filter((p) => Math.abs(p.deltaHours) >= 0.25);

  // E5: filter staff/shifts for the active view mode. The table render
  // below consumes filteredStaff; cell + edit logic stays unchanged.
  // Employee mode shows ALL staff (no drill-down) — every cell is
  // editable; a per-person section header is rendered between groups
  // so the rows visually cluster by person.
  const filteredStaff = (() => {
    if (viewMode === 'day') {
      return staff
        .map((p) => ({ ...p, shifts: (p.shifts || []).filter((s) => s.date === selectedDay) }))
        .filter((p) => p.shifts.length > 0);
    }
    return staff;
  })();
  // CJ 2026-06-30 diagnostic — Day view filter reports 10 shifts on
  // 06-22 but renders shifts on other days. Dump the render input so
  // we can see the divergence in the browser console.
  if (typeof window !== 'undefined' && viewMode === 'day') {
    // eslint-disable-next-line no-console
    console.log('[timecard-review:day-filter]', {
      selectedDay,
      filteredStaffCount: filteredStaff.length,
      totalShifts: filteredStaff.reduce((n, p) => n + p.shifts.length, 0),
      sample: filteredStaff.slice(0, 3).map((p) => ({
        name: p.name,
        shifts: p.shifts.map((s) => ({ date: s.date, actualStart: s.actualStart, actualEnd: s.actualEnd })),
      })),
    });
  }

  // E5: certify gate — only allow once the work week has fully ended in NY.
  const weekEndUtcMs = nyMidnightOfYmd(weekStart) + 7 * 86400000;
  const weekStillRunning = Date.now() < weekEndUtcMs;

  return (
    <div className="card" style={{ borderLeft: `4px solid ${certified ? '#2e7d32' : 'var(--forest)'}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700 }}>
            Time Card Review · {location.name} · week of {weekStart}
          </div>
          <div style={{ fontSize: 12, color: '#757575' }}>
            {timeclockOn
              ? 'Per-location review. Edits are written to the audit log.'
              : 'Read-only — punches come from Square. Review the deltas + issues, fix punches in Square, then certify here.'}
            {certified && (
              <span style={{ color: '#2e7d32', fontWeight: 600, marginLeft: 6 }}>
                · Certified by {certification.certified_by_name} on {certification.certified_at?.slice(0, 10)}
              </span>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {syncMsg && <span style={{ fontSize: 11, color: syncMsg.startsWith('Pulled') ? '#2e7d32' : '#c62828' }}>{syncMsg}</span>}
          {!certified && (
            <button onClick={syncFromSquare} disabled={syncBusy} className="btn-secondary"
              title="Pull this week's punches from Square (after you've fixed something there)"
              style={{ fontSize: 12 }}>
              {syncBusy ? 'Pulling…' : '↻ Pull from Square'}
            </button>
          )}
          {timeclockOn && <ManualHoursButton currentUser={currentUser} onAdded={load} />}
          <button onClick={onClose} className="btn-secondary" style={{ fontSize: 12 }}>Close</button>
        </div>
      </div>

      {/* Corrections are staff-submitted punch edits — retired with the clock. */}
      {timeclockOn && <CorrectionRequestQueue location={location} currentUser={currentUser} onApplied={load} />}

      {/* Totals header */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8, marginBottom: 12 }}>
        <PerfStat label="Scheduled" value={`${totals.scheduledHours.toFixed(1)} h`} />
        <PerfStat label="Worked"    value={`${totals.actualHours.toFixed(1)} h`} />
        <PerfStat label={`Delta (${overUnder})`}
          value={`${totals.deltaHours >= 0 ? '+' : ''}${totals.deltaHours.toFixed(1)} h`}
          accent={Math.abs(totals.deltaHours) > 1 ? '#ef6c00' : '#37474f'} />
        <PerfStat label="Cost of delta"
          value={totals.deltaCost ? `${totals.deltaCost >= 0 ? '+' : ''}$${Math.abs(totals.deltaCost).toFixed(2)}` : '—'}
          accent={totals.deltaCost > 0 ? '#c62828' : '#37474f'}
          hint={totals.rateMissing.length ? `Rate missing: ${totals.rateMissing.length} staff` : null} />
      </div>

      {dragOnDelta.length > 0 && (
        <div style={{ marginBottom: 8, padding: 8, background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 6, fontSize: 12 }}>
          <strong>Delta contributors:</strong>{' '}
          {dragOnDelta.map((p) => `${p.name} (${p.deltaHours >= 0 ? '+' : ''}${p.deltaHours.toFixed(1)}h)`).join(' · ')}
        </div>
      )}

      {/* E5: view-mode toggle (Week / Day / Employee) */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
        {[['week','Week'], ['day','Day'], ['employee','Employee']].map(([id, label]) => (
          <button key={id} onClick={() => setViewMode(id)}
            style={{ padding: '4px 12px', fontSize: 12, fontWeight: 600,
                     background: viewMode === id ? 'var(--forest)' : '#fff',
                     color: viewMode === id ? '#fff' : '#37474f',
                     border: `1px solid ${viewMode === id ? 'var(--forest)' : '#d0d4d9'}`,
                     borderRadius: 4, cursor: 'pointer', fontFamily: 'inherit' }}>
            {label}
          </button>
        ))}
        {viewMode === 'day' && (
          <select value={selectedDay} onChange={(e) => setSelectedDay(e.target.value)}
            style={{ padding: '4px 8px', fontSize: 12, border: '1px solid #d0d4d9',
                     borderRadius: 4, marginLeft: 8 }}>
            {weekDays.map((d) => {
              const dt = new Date(d + 'T12:00:00Z');
              const dow = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][dt.getUTCDay()];
              return <option key={d} value={d}>{dow} {d.slice(5)}</option>;
            })}
          </select>
        )}
        {viewMode === 'day' && (
          <span style={{ fontSize: 11, color: '#9e9e9e', marginLeft: 8 }}>
            showing {filteredStaff.length} staff · {filteredStaff.reduce((n, p) => n + p.shifts.length, 0)} shifts on {selectedDay}
          </span>
        )}
      </div>

      {/* Per-staff editable rows */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', fontSize: 12 }}>
          <thead>
            <tr style={{ color: '#757575', textAlign: 'left' }}>
              <th style={{ padding: '4px 8px', borderBottom: '1px solid #f0f0f0' }}>Day</th>
              <th style={{ padding: '4px 8px', borderBottom: '1px solid #f0f0f0' }}>Staff</th>
              <th style={{ padding: '4px 8px', borderBottom: '1px solid #f0f0f0' }}>Scheduled</th>
              <th style={{ padding: '4px 8px', borderBottom: '1px solid #f0f0f0' }}>Clock in</th>
              <th style={{ padding: '4px 8px', borderBottom: '1px solid #f0f0f0' }}>Clock out</th>
              <th style={{ padding: '4px 8px', borderBottom: '1px solid #f0f0f0', textAlign: 'right' }}>Δ</th>
            </tr>
          </thead>
          <tbody>
            {filteredStaff.length === 0 && (
              <tr><td colSpan={6} style={{ padding: 16, textAlign: 'center', color: '#9e9e9e' }}>
                No shifts to show for this {viewMode}.
              </td></tr>
            )}
            {filteredStaff.flatMap((p, pi) => {
              // E5: in Employee mode, render a section header row before each
              // person's shifts so rows cluster visually by person.
              const headerRow = viewMode === 'employee' ? (
                <tr key={`hdr-${p.user_id}`} style={{ background: '#f7f8fa' }}>
                  <td colSpan={6} style={{ padding: '6px 8px', fontWeight: 700, color: '#37474f', borderTop: pi === 0 ? 'none' : '2px solid #e5e8eb' }}>
                    {p.name} <span style={{ fontWeight: 400, color: '#9e9e9e', fontSize: 11 }}>· {p.role}</span>
                  </td>
                </tr>
              ) : null;
              const shiftRows = p.shifts.map((s, si) => {
              const sched = (s.scheduledStart && s.scheduledEnd) ? `${fmtClock(s.scheduledStart)}–${fmtClock(s.scheduledEnd)}` : '—';
              const date = new Date(s.date + 'T12:00:00Z');
              const dayLabel = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][date.getUTCDay()];
              const editKey = `${pi}|${si}`;
              return (
                <tr key={`${p.name}-${s.date}-${si}`} style={{ borderBottom: '1px solid #fafafa' }}>
                  <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>{dayLabel} {s.date.slice(5)}</td>
                  <td style={{ padding: '6px 8px' }}>
                    <div style={{ fontWeight: 600 }}>{p.name}</div>
                    <div style={{ fontSize: 10, color: '#9e9e9e' }}>{p.role}</div>
                  </td>
                  <td style={{ padding: '6px 8px', color: '#757575' }}>{sched}</td>
                  <td style={{ padding: '6px 8px' }}>
                    <EditableTime value={s.actualStart}
                      disabled={certified || !timeclockOn}
                      onSave={(v) => saveEdit(p, s, 'clock_in', v)} />
                  </td>
                  <td style={{ padding: '6px 8px' }}>
                    <EditableTime value={s.actualEnd}
                      disabled={certified || !timeclockOn}
                      onSave={(v) => saveEdit(p, s, 'clock_out', v)} />
                    {/* Break visibility + edit — JT manager 2026-06-08 had no
                        way to see whether Chris's unpaid breaks made it
                        through the Square import. Show paid + unpaid break
                        minutes when present; flag the absence of any break
                        on long shifts; let the manager set/clear an unpaid
                        break inline so a missed Square break is fixable from
                        this row. */}
                    {(() => {
                      const u = Number(s.break_unpaid_minutes) || 0;
                      const pd = Number(s.break_paid_minutes) || 0;
                      const startMin = s.actualStart ? Number(s.actualStart.split(':')[0]) * 60 + Number(s.actualStart.split(':')[1]) : null;
                      const endMin   = s.actualEnd   ? Number(s.actualEnd.split(':')[0])   * 60 + Number(s.actualEnd.split(':')[1])   : null;
                      const grossMin = (startMin != null && endMin != null && endMin > startMin) ? endMin - startMin : null;
                      const longShiftNoBreak = grossMin != null && grossMin >= 5 * 60 && u === 0 && pd === 0;
                      const showAnything = u > 0 || pd > 0 || longShiftNoBreak || s.monarch_punch_id;
                      if (!showAnything) return null;
                      async function setUnpaidBreak() {
                        if (certified || !timeclockOn) return;
                        if (!s.monarch_punch_id) {
                          toast.error('Add a clock-in/out first before assigning a break.');
                          return;
                        }
                        const current = String(u || '');
                        const raw = window.prompt(
                          `Unpaid break minutes for this shift?\n\nLeave blank or 0 to clear.`,
                          current,
                        );
                        if (raw === null) return;
                        const mins = raw.trim() === '' ? 0 : Number(raw);
                        if (!Number.isFinite(mins) || mins < 0 || mins > 480) {
                          toast.error('Enter 0–480 minutes.');
                          return;
                        }
                        try {
                          await api('PUT', `/api/admin/timeclock/punches/${s.monarch_punch_id}`,
                            { break_unpaid_minutes: Math.round(mins) }, headers);
                          load();
                        } catch (e) { toast.error(errText(e, 'Could not save break.')); }
                      }
                      return (
                        <div style={{ marginTop: 2, fontSize: 10, color: '#757575', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
                          {u > 0 && <span title={`${u}-minute unpaid break (deducted from hours)`} style={{ color: '#bf360c' }}>🍽 {u}m unpaid</span>}
                          {pd > 0 && <span title={`${pd}-minute paid break`} style={{ color: '#558b2f' }}>☕ {pd}m paid</span>}
                          {longShiftNoBreak && (
                            <span title="Shift is 5+ hours but no break was recorded — check Square or add one manually."
                                  style={{ color: '#bf360c', fontWeight: 600 }}>
                              ⚠ no break recorded
                            </span>
                          )}
                          {s.monarch_punch_id && canEditPunches && (
                            <button type="button" onClick={setUnpaidBreak}
                              style={{ background: 'transparent', border: '1px dashed #d0d4d9', borderRadius: 4,
                                       padding: '1px 6px', fontSize: 10, color: '#5a6470', cursor: 'pointer' }}>
                              {u > 0 ? 'edit unpaid break' : '+ unpaid break'}
                            </button>
                          )}
                          {s.monarch_punch_id && canEditPunches && allLocations.length > 1 && (
                            <select
                              value=""
                              disabled={movingKey === s.monarch_punch_id}
                              title="Move this shift to the café it should have been at"
                              onChange={(e) => { const v = e.target.value; if (v) moveLocation(p, s, Number(v)); e.target.value = ''; }}
                              style={{ background: 'transparent', border: '1px dashed #d0d4d9', borderRadius: 4,
                                       padding: '1px 6px', fontSize: 10, color: '#5a6470', cursor: 'pointer', fontFamily: 'inherit' }}>
                              <option value="">{movingKey === s.monarch_punch_id ? 'moving…' : '→ move café'}</option>
                              {allLocations.filter((l) => l.id !== data.meta.location.id).map((l) => (
                                <option key={l.id} value={l.id}>{l.name}</option>
                              ))}
                            </select>
                          )}
                          {s.monarch_punch_id && canEditPunches && (
                            <button type="button"
                              title="Delete this punch entirely (wrong entry, duplicate, etc.)"
                              onClick={() => deletePunch(p, s)}
                              style={{ background: 'transparent', border: '1px dashed #fecaca', borderRadius: 4,
                                       padding: '1px 6px', fontSize: 10, color: '#b91c1c', cursor: 'pointer',
                                       fontFamily: 'inherit' }}>
                              × delete
                            </button>
                          )}
                        </div>
                      );
                    })()}
                    {/* CJ 2026-06-15: + punch inline for split-shift
                        forgot-to-clock-back-in case. Hidden when the
                        week is already certified. */}
                    {canEditPunches && (addPunchRow === editKey ? (
                      <div style={{ marginTop: 6, padding: 6, background: '#fafafa',
                                      border: '1px dashed #d0d4d9', borderRadius: 4,
                                      display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                        <input type="time" value={addPunchForm.in_time}
                          onChange={e => setAddPunchForm(f => ({ ...f, in_time: e.target.value }))}
                          style={{ fontSize: 11, padding: '2px 4px', border: '1px solid #d0d4d9', borderRadius: 3, width: 80 }} />
                        <span style={{ fontSize: 10, color: '#9e9e9e' }}>–</span>
                        <input type="time" value={addPunchForm.out_time}
                          onChange={e => setAddPunchForm(f => ({ ...f, out_time: e.target.value }))}
                          style={{ fontSize: 11, padding: '2px 4px', border: '1px solid #d0d4d9', borderRadius: 3, width: 80 }} />
                        <button onClick={() => addPunch(p, s)} disabled={addPunchBusy}
                          style={{ fontSize: 10, padding: '2px 8px', background: '#16a34a', color: '#fff',
                                    border: 'none', borderRadius: 3, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600 }}>
                          {addPunchBusy ? '…' : 'Save'}
                        </button>
                        <button onClick={() => { setAddPunchRow(null); setAddPunchForm({ in_time: '', out_time: '' }); }}
                          style={{ fontSize: 10, padding: '2px 6px', background: 'transparent', color: '#5a6470',
                                    border: '1px solid #d0d4d9', borderRadius: 3, cursor: 'pointer', fontFamily: 'inherit' }}>
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button type="button"
                        title="Add another in/out for this day (split shift, forgot to clock back in, etc.)"
                        onClick={() => {
                          setAddPunchRow(editKey);
                          // Default the inline form to the next likely
                          // window after the existing clock-out (the
                          // common 12-2 case after an 11:30 lunch).
                          setAddPunchForm({
                            in_time:  s.actualEnd || s.scheduledEnd || '12:00',
                            out_time: s.scheduledEnd || '',
                          });
                        }}
                        style={{ marginTop: 4, background: 'transparent', border: '1px dashed #d0d4d9', borderRadius: 4,
                                  padding: '2px 8px', fontSize: 10, color: '#5a6470', cursor: 'pointer',
                                  fontFamily: 'inherit' }}>
                        + punch
                      </button>
                    ))}
                  </td>
                  <td style={{ padding: '6px 8px', textAlign: 'right', whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                    {(() => {
                      const flags = [];
                      // Forgot to clock in.
                      if (s.clockStatus === 'not_clocked_in') {
                        flags.push({ text: 'no clock-in', color: '#c62828' });
                      }
                      // Late clock-in (>5 min after scheduled start).
                      if (s.lateMinutes > 5) {
                        flags.push({ text: `late ${fmtMins(s.lateMinutes)}`, color: '#ef6c00' });
                      }
                      // Forgot to clock out — actualStart present, actualEnd null,
                      // and the shift's scheduled end has already passed. Catches
                      // the case where someone clocked in but never out.
                      if (s.actualStart && !s.actualEnd && s.scheduledEnd) {
                        // Day in the past or shift's end time has passed today.
                        const sched = new Date(`${s.date}T${s.scheduledEnd}:00`);
                        if (sched < new Date()) {
                          flags.push({ text: 'no clock-out', color: '#c62828' });
                        }
                      }
                      // Clocked out late (>5 min after scheduled end).
                      if (s.overtimeMinutes > 5) {
                        flags.push({ text: `late out ${s.overtimeMinutes}m`, color: '#ef6c00' });
                      }
                      // Clocked out early (>5 min before scheduled end).
                      if (s.earlyOutMinutes > 5) {
                        flags.push({ text: `early ${fmtMins(s.earlyOutMinutes)}`, color: '#ef6c00' });
                      }
                      // Still on clock past threshold (overtime status — actualEnd missing
                      // but no schedule end yet, or running long).
                      if (s.clockStatus === 'overtime' && !s.actualEnd && s.overtimeMinutes <= 5) {
                        flags.push({ text: 'on clock', color: '#1565c0' });
                      }
                      if (flags.length === 0) return <span style={{ color: '#9e9e9e' }}>—</span>;
                      return (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                          {flags.map((f, i) => (
                            <span key={i} style={{ color: f.color, fontWeight: 600, fontSize: 11 }}>{f.text}</span>
                          ))}
                        </div>
                      );
                    })()}
                  </td>
                </tr>
              );
              });
              return headerRow ? [headerRow, ...shiftRows] : shiftRows;
            })}
          </tbody>
        </table>
      </div>

      {/* Edit log */}
      {edits.length > 0 && (
        <div style={{ marginTop: 12, paddingTop: 8, borderTop: '1px solid #f0f0f0' }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#757575', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 4 }}>
            Edit log ({edits.length})
          </div>
          <div style={{ fontSize: 11, color: '#757575' }}>
            {edits.slice(0, 10).map((e) => (
              <div key={e.id} style={{ padding: '2px 0' }}>
                {e.created_at?.slice(0, 16)} · {e.field}: {e.before_value || '—'} → {e.after_value || '—'} · by {e.edited_by_name}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Certify */}
      {(() => {
        // W-109: count incomplete punches so we can warn / gate the
        // certify action when something's clearly missing. Staff who
        // clocked in but never out, or were scheduled and never clocked
        // in at all, are the two cases that meaningfully change accrual.
        // CJ 2026-06-30: staff who miss a shift entirely can be marked
        // absent (excused/unexcused) via markAbsent — filter those
        // (user, date) pairs out so certify unblocks.
        const absenceKeys = new Set(
          (data.absences || []).map((a) => `${a.user_id}|${a.shift_date}`)
        );
        const missingPunches = [];
        for (const p of staff) {
          for (const s of (p.shifts || [])) {
            const sched = s.scheduledEnd ? new Date(`${s.date}T${s.scheduledEnd}:00`) : null;
            const sEnd = sched && sched < new Date();
            const isAbsent = absenceKeys.has(`${p.user_id}|${s.date}`);
            if (isAbsent) continue;
            if (s.actualStart && !s.actualEnd && sEnd) {
              missingPunches.push({ name: p.name, user_id: p.user_id, date: s.date, reason: 'no clock-out' });
            } else if (s.scheduledStart && !s.actualStart && sEnd) {
              missingPunches.push({ name: p.name, user_id: p.user_id, date: s.date, reason: 'no clock-in' });
            }
          }
        }
        const blocking = missingPunches.length > 0;
        return (
          <>
            {blocking && !certified && !certResult && (
              <div style={{ marginTop: 14, padding: 10, background: '#fff3e0',
                              border: '1px solid #ffcc80', borderRadius: 6, fontSize: 12 }}>
                <div style={{ fontWeight: 700, color: '#bf360c', marginBottom: 6 }}>
                  {missingPunches.length} unresolved punch issue{missingPunches.length === 1 ? '' : 's'} — fix before certifying:
                </div>
                <div style={{ margin: '4px 0 0 0', padding: 0, color: '#4e342e', display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {missingPunches.map((m, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ flex: 1 }}>{m.name} · {m.date} · {m.reason}</span>
                      {m.user_id && m.reason === 'no clock-in' && (
                        <>
                          <button onClick={() => markAbsent(m.user_id, m.date, 'excused')}
                            title="Approved absence — no reliability penalty"
                            style={{ fontSize: 11, padding: '2px 8px', background: '#fff', color: '#2e7d32',
                                      border: '1px solid #bbf7d0', borderRadius: 4, cursor: 'pointer', fontFamily: 'inherit' }}>
                            Mark excused
                          </button>
                          <button onClick={() => markAbsent(m.user_id, m.date, 'unexcused')}
                            title="No-call-no-show — emits reliability incident"
                            style={{ fontSize: 11, padding: '2px 8px', background: '#fff', color: '#b91c1c',
                                      border: '1px solid #fecaca', borderRadius: 4, cursor: 'pointer', fontFamily: 'inherit' }}>
                            Mark no-show
                          </button>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div style={{ marginTop: 14, paddingTop: 10, borderTop: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
              {certResult ? (
                <div style={{ flex: 1, fontSize: 12 }}>
                  <div style={{ fontWeight: 600, color: '#2e7d32', marginBottom: 4 }}>
                    {certResult.alreadyCertified ? 'Already certified.' : 'Certified.'}
                  </div>
                  {certResult.accrued && certResult.accrued.length > 0 && (
                    <div style={{ color: '#37474f' }}>
                      PTO accrued for {certResult.accrued.length} staff:
                      {' '}
                      {certResult.accrued.map((a) => `${a.name} (+${a.accrual_hours}h)`).join(' · ')}
                    </div>
                  )}
                  {certResult.accrued && certResult.accrued.length === 0 && !certResult.alreadyCertified && (
                    <div style={{ color: '#9e9e9e' }}>
                      No accrual yet — staff who worked here this week also worked at locations that aren't certified.
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ fontSize: 12, color: '#757575' }}>
                  {certified
                    ? 'Certified. Reopen with Un-certify if you need to fix something — accrual reverses and re-fires on re-certify.'
                    : 'Once it looks right, certify the week — one press. The week locks and PTO accrues automatically; you can un-certify to reopen.'}
                </div>
              )}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                {(certified || certResult) && (
                  <button onClick={uncertify} disabled={uncertBusy}
                    title="Reopen this week — reverses its PTO accrual + reliability marks so you can fix and re-certify"
                    style={{ padding: '8px 14px', fontSize: 13, fontWeight: 600,
                              background: '#fff', color: '#b25a1f',
                              border: '1px solid #d9a066', borderRadius: 6, cursor: uncertBusy ? 'default' : 'pointer', fontFamily: 'inherit' }}>
                    {uncertBusy ? 'Reopening…' : 'Un-certify'}
                  </button>
                )}
                {canEditPunches && (
                  <button onClick={openUnsched}
                    style={{ padding: '8px 14px', fontSize: 13, fontWeight: 600,
                              background: '#fff', color: 'var(--forest)',
                              border: '1px solid var(--forest)', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit' }}>
                    + Add unscheduled punch
                  </button>
                )}
                {/* One-button certify (CJ 2026-07-30): no typed confirmation —
                    the missed-clock block still guards it, and it's now
                    reversible via Uncertify, so a single press is safe. */}
                <button onClick={certify}
                  disabled={certBusy || certified || !!certResult || blocking || weekStillRunning}
                  title={weekStillRunning
                    ? 'You can certify after Sunday 11:59pm — the work week is still in progress.'
                    : (blocking ? 'Resolve the missing punches above before certifying' : '')}
                  style={{
                    padding: '8px 18px', fontSize: 13, fontWeight: 700,
                    background: certified ? '#9e9e9e' : 'var(--forest)',
                    color: '#fff', border: 'none', borderRadius: 6,
                    cursor: (certified || certBusy || blocking || weekStillRunning) ? 'not-allowed' : 'pointer',
                    opacity: (certified || certBusy || blocking || weekStillRunning) ? 0.5 : 1,
                  }}>
                  {certBusy ? 'Certifying…'
                    : certified ? 'Certified'
                    : weekStillRunning ? 'Certify (after Sunday)'
                    : 'Certify week'}
                </button>
              </div>
            </div>
            {unschedOpen && (
              <div onClick={() => !unschedBusy && setUnschedOpen(false)}
                style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
                          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
                <div onClick={(e) => e.stopPropagation()}
                  style={{ background: '#fff', borderRadius: 10, padding: 22, maxWidth: 480, width: '100%' }}>
                  <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 6 }}>
                    Add unscheduled punch
                  </div>
                  <div style={{ fontSize: 12, color: '#757575', marginBottom: 12 }}>
                    For a staff member who worked at <strong>{location.name}</strong> last week without a scheduled shift.
                  </div>
                  <div style={{ display: 'grid', gap: 10, marginBottom: 14 }}>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#37474f' }}>
                      Staff member
                      <select value={unschedForm.user_id}
                        onChange={(e) => setUnschedForm((f) => ({ ...f, user_id: e.target.value }))}
                        style={{ display: 'block', width: '100%', marginTop: 4, padding: '6px 8px',
                                  border: '1px solid #d0d4d9', borderRadius: 6, fontSize: 13, fontFamily: 'inherit' }}>
                        <option value="">— select —</option>
                        {[...unschedUsers]
                          .filter((u) => (u.status || 'active') === 'active')
                          .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                          .map((u) => (
                            <option key={u.id} value={u.id}>{u.name}{u.role ? ` · ${u.role}` : ''}</option>
                          ))}
                      </select>
                    </label>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#37474f' }}>
                      Date
                      <select value={unschedForm.date}
                        onChange={(e) => setUnschedForm((f) => ({ ...f, date: e.target.value }))}
                        style={{ display: 'block', width: '100%', marginTop: 4, padding: '6px 8px',
                                  border: '1px solid #d0d4d9', borderRadius: 6, fontSize: 13, fontFamily: 'inherit' }}>
                        {weekDays.map((d) => (<option key={d} value={d}>{d}</option>))}
                      </select>
                    </label>
                    <div style={{ display: 'flex', gap: 10 }}>
                      <label style={{ flex: 1, fontSize: 12, fontWeight: 600, color: '#37474f' }}>
                        Clock-in
                        <input type="time" value={unschedForm.in_time}
                          onChange={(e) => setUnschedForm((f) => ({ ...f, in_time: e.target.value }))}
                          style={{ display: 'block', width: '100%', marginTop: 4, padding: '6px 8px',
                                    border: '1px solid #d0d4d9', borderRadius: 6, fontSize: 13, fontFamily: 'inherit' }} />
                      </label>
                      <label style={{ flex: 1, fontSize: 12, fontWeight: 600, color: '#37474f' }}>
                        Clock-out
                        <input type="time" value={unschedForm.out_time}
                          onChange={(e) => setUnschedForm((f) => ({ ...f, out_time: e.target.value }))}
                          style={{ display: 'block', width: '100%', marginTop: 4, padding: '6px 8px',
                                    border: '1px solid #d0d4d9', borderRadius: 6, fontSize: 13, fontFamily: 'inherit' }} />
                      </label>
                    </div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                    <button onClick={() => setUnschedOpen(false)} disabled={unschedBusy}
                      className="btn-secondary" style={{ fontSize: 13 }}>Cancel</button>
                    <button onClick={saveUnsched} disabled={unschedBusy}
                      style={{ padding: '8px 16px', fontSize: 13, fontWeight: 700, background: 'var(--forest)',
                                color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer',
                                opacity: unschedBusy ? 0.5 : 1 }}>
                      {unschedBusy ? 'Saving…' : 'Add punch'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        );
      })()}
    </div>
  );
}
